// RichTextEditor.jsx

import React, {
    forwardRef,
    useEffect,
    useImperativeHandle,
    useMemo,
    useRef,
    useState,
} from 'react';

// Context
import { useTheme } from 'context/ThemeContext';

// Utilities
import {
    buildEmbeddedNodeAttrs,
    createEmbeddedNodeBlock,
    createRichTextValue,
    normalizeRichTextValue,
    RICH_TEXT_IMAGE_TYPE,
    serializeRichTextDocument,
} from 'utilities/RichText';
import { parseHelpImageWidth } from './richTextImageUtils';

// CSS
import './RichTextEditor.css';

// Icons
import Icon from 'svg/Icon';

// Components
import { createRichTextExtensions } from './richTextTiptap';
import { EditorContent, useEditor } from '@tiptap/react';

const ToolbarButton = ({ iconKey, active = false, disabled = false, label, onClick }) => {

        // ----- CONTEXT ----- //

        const { theme } = useTheme();

        // ----- HANDLERS ----- //

        /**
         * handleMouseDown
         *
         * Handles mouse down.
         *
         * @param {object} event - event.
         */
        const handleMouseDown = (event) => {
                  event.preventDefault();
    };

        // ----- RENDER ----- //

        return (
        <button
          className="rich-text-editor-toolbar-button"
          style={{
                backgroundColor: active ? theme.accent + '22' : 'transparent',
                borderColor: active ? theme.accent : theme.backgroundHover,
                color: active ? theme.foreground : theme.foregroundMuted,
            }}
          disabled={disabled}
          type="button"
          onClick={onClick}
          onMouseDown={handleMouseDown}
        >
            {iconKey && (
                <Icon
                  iconKey={iconKey}
                  width={20}
                  height={20}
                  color={active ? theme.foreground : theme.foregroundMuted}
                />
            )}
            {label}
        </button>
    );
};

/**
 * RichTextEditor
 *
 * Renders the Tiptap editor used for schema rich text fields.
 *
 * @param {boolean} autoFocus - Whether the editor should focus itself when ready.
 * @param {string|number} [autoFocusKey] - When this changes while autoFocus is on, refocus at end.
 * @param {boolean} compact - Whether to render the compact editor height.
 * @param {boolean} disabled - Whether the editor is read-only.
 * @param {function} onBlur - Called when the editor loses focus.
 * @param {function} onChange - Called with the normalized rich text value.
 * @param {function} onImageUpload - Uploads a pasted or chosen image and returns node attrs.
 * @param {function} onInsertNodeRequest - Opens the insert-node flow.
 * @param {string} placeholder - Placeholder text for an empty editor.
 * @param {boolean} showInsertButton - Whether to show the insert button.
 * @param {boolean} stopInteractionPropagation - Whether editor pointer and drag interactions should be contained.
 * @param {string} toolbarPreset - Toolbar preset name.
 * @param {object|null} value - Current normalized or raw rich text value.
 *
 * @returns {ReactNode} The rendered editor.
 */
const RichTextEditor = forwardRef(({
    autoFocus = false,
    autoFocusKey = '',
    compact = false,
    disabled = false,
    onBlur,
    onChange,
    onImageUpload,
    onInsertNodeRequest,
    placeholder = 'Write something...',
    showInsertButton = true,
    stopInteractionPropagation = false,
    toolbarPreset = 'field',
    value,
}, ref) => {

    // Theme Context
    const { theme } = useTheme();

    // Local rerender tick for selection-driven toolbar state.
    const [editorVersion, setEditorVersion] = useState(0);

    const normalizedValue = useMemo(() => normalizeRichTextValue(value), [value]);
    const serializedContent = useMemo(() => serializeRichTextDocument(normalizedValue.content), [normalizedValue.content]);
    const compactClassName = compact ? ' is-compact' : '';

    const blurCommitFrameRef = useRef(null);
    const editorRef = useRef(null);
    const imageInputRef = useRef(null);
    const onChangeRef = useRef(onChange);
    const onBlurRef = useRef(onBlur);
    const onImageUploadRef = useRef(onImageUpload);

    const [imageUploading, setImageUploading] = useState(false);

    useEffect(() => {
        onChangeRef.current = onChange;
    }, [onChange]);

    useEffect(() => {
        onBlurRef.current = onBlur;
    }, [onBlur]);

    useEffect(() => {
        onImageUploadRef.current = onImageUpload;
    }, [onImageUpload]);

    const collectImageFiles = (fileList) => (
        Array.from(fileList || []).filter((file) => (
            file instanceof Blob && String(file.type || '').startsWith('image/')
        ))
    );

    /**
     * Uploads image files and inserts them at the current cursor.
     *
     * @param {FileList|File[]} fileList - Image files from paste, drop, or picker.
     * @returns {Promise<boolean>}
     */
    const uploadAndInsertImages = async (fileList) => {
        const editorInstance = editorRef.current;
        const upload = onImageUploadRef.current;
        const files = collectImageFiles(fileList);
        if (!editorInstance || typeof upload !== 'function' || files.length === 0) {
            return false;
        }

        setImageUploading(true);
        try {
            for (const file of files) {
                const attrs = await upload(file);
                if (!attrs) continue;
                editorInstance
                    .chain()
                    .focus()
                    .insertContent({
                        type: RICH_TEXT_IMAGE_TYPE,
                        attrs: {
                            alt: attrs.alt || '',
                            src: attrs.src || '',
                            storagePath: attrs.storagePath || '',
                            width: parseHelpImageWidth(attrs.width),
                        },
                    })
                    .run();
            }
            return true;
        } catch (error) {
            console.error('[RichTextEditor] image upload failed:', error);
            return false;
        } finally {
            setImageUploading(false);
        }
    };

    useEffect(() => {
        return () => {
            if (blurCommitFrameRef.current != null) {
                cancelAnimationFrame(blurCommitFrameRef.current);
            }
        };
    }, []);

    const editor = useEditor({
        content: normalizedValue.content,
        editable: !disabled,
        editorProps: {
            attributes: {
                class: `rich-text-editor-content${compactClassName}`,
            },
            handleDrop: (_view, event) => {
                const files = collectImageFiles(event.dataTransfer?.files);
                if (!files.length) return false;
                if (typeof onImageUploadRef.current !== 'function') return true;
                event.preventDefault();
                void uploadAndInsertImages(files);
                return true;
            },
            handlePaste: (_view, event) => {
                const files = collectImageFiles(event.clipboardData?.files);
                if (!files.length) return false;
                if (typeof onImageUploadRef.current !== 'function') return true;
                event.preventDefault();
                void uploadAndInsertImages(files);
                return true;
            },
        },
        extensions: createRichTextExtensions({ placeholder }),
        immediatelyRender: false,
        onBlur: ({ editor: nextEditor }) => {
            const nextValue = createRichTextValue(nextEditor.getJSON());

            if (blurCommitFrameRef.current != null) {
                cancelAnimationFrame(blurCommitFrameRef.current);
            }

            blurCommitFrameRef.current = requestAnimationFrame(() => {
                blurCommitFrameRef.current = null;
                onBlurRef.current?.(nextValue);
            });
        },
        onUpdate: ({ editor: nextEditor }) => {
            onChangeRef.current?.(createRichTextValue(nextEditor.getJSON()));
        },
    }, [compactClassName, disabled, placeholder]);

    useEffect(() => {
        editorRef.current = editor || null;
    }, [editor]);

    // Keep the editor content synced with external updates.
    useEffect(() => {
        if (!editor) return;

        const currentSerialized = serializeRichTextDocument(editor.getJSON());
        if (currentSerialized === serializedContent) return;

        editor.commands.setContent(normalizedValue.content, false);
    }, [editor, normalizedValue.content, serializedContent]);

    // Focus at end of document (Peek NOTE open, node switch, etc.).
    useEffect(() => {
        if (!autoFocus || !editor || disabled) return;

        const frameId = requestAnimationFrame(() => {
            editor.chain().focus('end').run();
        });

        return () => {
            cancelAnimationFrame(frameId);
        };
    }, [autoFocus, autoFocusKey, disabled, editor]);

    // Force local rerenders for toolbar active states.
    useEffect(() => {
        if (!editor) return undefined;

        /**
         * handleEditorStateChange
         *
         * Handles editor state change.
         */
        const handleEditorStateChange = () => {
            setEditorVersion((current) => current + 1);
        };

        editor.on('selectionUpdate', handleEditorStateChange);
        editor.on('transaction', handleEditorStateChange);

        return () => {
            editor.off('selectionUpdate', handleEditorStateChange);
            editor.off('transaction', handleEditorStateChange);
        };
    }, [editor]);

    const isBoldActive = editorVersion >= 0 && editor?.isActive('bold');
    const isItalicActive = editorVersion >= 0 && editor?.isActive('italic');
    const isUnderlineActive = editorVersion >= 0 && editor?.isActive('underline');
    const isBulletListActive = editorVersion >= 0 && editor?.isActive('bulletList');
    const isBlockquoteActive = editorVersion >= 0 && editor?.isActive('blockquote');
    const isCodeBlockActive = editorVersion >= 0 && editor?.isActive('codeBlock');
    const isHeadingOneActive = editorVersion >= 0 && editor?.isActive('heading', { level: 1 });
    const isOrderedListActive = editorVersion >= 0 && editor?.isActive('orderedList');
    const isHeadingTwoActive = editorVersion >= 0 && editor?.isActive('heading', { level: 2 });
    const isHeadingThreeActive = editorVersion >= 0 && editor?.isActive('heading', { level: 3 });
    const isArticleToolbar = toolbarPreset === 'article';

    /**
     * Toggles a heading level on the current selection.
     *
     * @param {number} level - Heading level.
     */
    const handleToggleHeading = (level) => {
        editor?.chain().focus().toggleHeading({ level }).run();
    };

    /**
     * handleStopInteractionPropagation
     *
     * Prevents inline editor pointer interactions from bubbling into draggable page editor shells.
     *
     * @param {object} event - React synthetic event.
     */
    const handleStopInteractionPropagation = (event) => {
        if (!stopInteractionPropagation) return;

        event.stopPropagation();
    };

    /**
     * handlePreventEditorDragStart
     *
     * Cancels native drag starts inside the editor so text selection does not trigger parent drag behavior.
     *
     * @param {object} event - React synthetic drag event.
     */
    const handlePreventEditorDragStart = (event) => {
        if (!stopInteractionPropagation) return;

        event.preventDefault();
        event.stopPropagation();
    };

    useImperativeHandle(ref, () => ({
        focus: () => {
            editor?.chain().focus('end').run();
        },
        insertEmbeddedNode: (node) => {
            if (!editor || !node) return false;

            const attrs = buildEmbeddedNodeAttrs(node);
            if (!attrs.nodeKey) return false;

            editor
                .chain()
                .focus()
                .insertContent([
                    createEmbeddedNodeBlock(attrs),
                    { type: 'paragraph' },
                ])
                .run();

            return true;
        },
        insertImage: (attrs) => {
            if (!editor || !attrs) return false;
            editor
                .chain()
                .focus()
                .insertContent({
                    type: RICH_TEXT_IMAGE_TYPE,
                    attrs: {
                        alt: attrs.alt || '',
                        src: attrs.src || '',
                        storagePath: attrs.storagePath || '',
                        width: parseHelpImageWidth(attrs.width),
                    },
                })
                .run();
            return true;
        },
    }), [editor]);

    return (
        <div
          className="rich-text-editor-container"
          style={{
            '--rich-text-selected-border': theme.accent,
          }}
          onClick={handleStopInteractionPropagation}
          onDoubleClick={handleStopInteractionPropagation}
          onDragStart={handlePreventEditorDragStart}
          onMouseDown={handleStopInteractionPropagation}
        >

            {/* TOOLBAR */}
            <div
              className="rich-text-editor-toolbar"
            >
                <ToolbarButton
                  iconKey="format_h1"
                  active={isHeadingOneActive}
                  disabled={disabled}
                  onClick={() => handleToggleHeading(1)}
                />
                <ToolbarButton
                  iconKey="format_h2"
                  active={isHeadingTwoActive}
                  disabled={disabled}
                  onClick={() => handleToggleHeading(2)}
                />
                <ToolbarButton
                  iconKey="format_h3"
                  active={isHeadingThreeActive}
                  disabled={disabled}
                  onClick={() => handleToggleHeading(3)}
                />
                <ToolbarButton
                  iconKey="format_bold"
                  active={isBoldActive}
                  disabled={disabled}
                  onClick={() => editor?.chain().focus().toggleBold().run()}
                />
                <ToolbarButton
                  iconKey="format_italic"
                  active={isItalicActive}
                  disabled={disabled}
                  onClick={() => editor?.chain().focus().toggleItalic().run()}
                />
                <ToolbarButton
                  iconKey="format_underlined"
                  active={isUnderlineActive}
                  disabled={disabled}
                  onClick={() => editor?.chain().focus().toggleUnderline().run()}
                />
                <ToolbarButton
                  iconKey="format_list_bulleted"
                  active={isBulletListActive}
                  disabled={disabled}
                  onClick={() => editor?.chain().focus().toggleBulletList().run()}
                />
                <ToolbarButton
                  iconKey="format_list_numbered"
                  active={isOrderedListActive}
                  disabled={disabled}
                  onClick={() => editor?.chain().focus().toggleOrderedList().run()}
                />
                {isArticleToolbar && (
                    <>
                        <ToolbarButton
                          iconKey="format_quote"
                          active={isBlockquoteActive}
                          disabled={disabled}
                          onClick={() => editor?.chain().focus().toggleBlockquote().run()}
                        />
                        <ToolbarButton
                          iconKey="code"
                          active={isCodeBlockActive}
                          disabled={disabled}
                          onClick={() => editor?.chain().focus().toggleCodeBlock().run()}
                        />
                        <ToolbarButton
                          iconKey="horizontal_rule"
                          disabled={disabled}
                          onClick={() => editor?.chain().focus().setHorizontalRule().run()}
                        />
                        <ToolbarButton
                          iconKey="undo"
                          disabled={disabled}
                          onClick={() => editor?.chain().focus().undo().run()}
                        />
                        <ToolbarButton
                          iconKey="redo"
                          disabled={disabled}
                          onClick={() => editor?.chain().focus().redo().run()}
                        />
                        {typeof onImageUpload === 'function' ? (
                            <ToolbarButton
                              iconKey="photo"
                              disabled={disabled || imageUploading}
                              onClick={() => imageInputRef.current?.click()}
                            />
                        ) : null}
                    </>
                )}

                {/* TOOLBAR SPACER 
                <div className="rich-text-editor-toolbar-spacer" />
                {showInsertButton ? (
                    <ToolbarButton
                      iconKey="variable_insert"
                      disabled={disabled || typeof onInsertNodeRequest !== 'function'}
                      label="Insert"
                      onClick={onInsertNodeRequest}
                    />
                ) : null}
                 */}
            </div>

            {/* EDITOR */}
            <div
              className={`rich-text-editor-surface${compactClassName}`}
            >
                <EditorContent editor={editor} />
            </div>

            {typeof onImageUpload === 'function' ? (
                <input
                  accept="image/png,image/jpeg,image/webp,image/gif"
                  className="rich-text-editor-image-input"
                  ref={imageInputRef}
                  type="file"
                  multiple
                  onChange={(event) => {
                      const files = collectImageFiles(event.target.files);
                      event.target.value = '';
                      if (files.length) void uploadAndInsertImages(files);
                  }}
                />
            ) : null}
        </div>
    );
});

// Dev Tools
RichTextEditor.displayName = 'RichTextEditor';

export default RichTextEditor;
